import { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import AddBottomButton from '../../../components/Add/AddBottomButton'
import AddModel from '../../../components/Add/AddModel'
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs'
import AddInputs from '../../../components/Add/AddInputs'
import CustomRadioButton from '../../../components/buttons/CustomRadioButton'
import useApi from '../../../services/useApi'
import endpoints from '../../../services/endpoints'

// Only "E Top Up Stocks" is wired up for MM-3054 - "Digital Wallet" purchases are
// shown (per design) but disabled until that wallet type is supported.
const WALLET_TYPES = [
  { id: 'etopup', label: 'E Top Up Stocks' },
  { id: 'digital_wallet', label: 'Digital Wallet' },
]

interface BundleActivationDraft {
  phone_number?: string
  country_code?: string
  mno_id?: string
  telecom_name?: string
  wallet_type?: string
}

function BundleActivation({ testing = false }: { testing?: boolean } = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { getAPI } = useApi()
  // Restored when the user backtracks here (e.g. via breadcrumbs) from the plans page,
  // so a phone number they already entered and had detected isn't wiped out.
  const draft = (location.state || {}) as BundleActivationDraft
  const [phoneNumber, setPhoneNumber] = useState<string>(draft.phone_number || '')
  const [countryCode, setCountryCode] = useState<string>(draft.country_code || '')
  const [mnoData, setMnoData] = useState<{ id?: string; name?: string }>(
    draft.mno_id ? { id: draft.mno_id, name: draft.telecom_name } : {}
  )
  const [prevFirstTwoDigits, setPrevFirstTwoDigits] = useState<string>('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [buttonDisabled, setButtonDisabled] = useState(!draft.mno_id)
  const [walletType, setWalletType] = useState<string>(draft.wallet_type || '')

  const inputs = {
    '': {
      'Phone Number': {
        required: true,
        disabled: false,
        id: 'phone_number',
        type: 'phone_number_no_verify',
        tabIndex: 0,
      },
      Telecom: {
        required: false,
        disabled: true,
        id: 'telecom',
        type: 'input',
        tabIndex: 1,
        placeholder: 'Telecom',
      },
    },
  }

  const debouncedApiCall = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout
      return (countryCode: string, firstTwoDigits: string) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(async () => {
          if (testing) {
            setMnoData({ id: 'test-mno-id', name: 'MTN' })
            setErrors({})
            setButtonDisabled(false)
            return
          }
          const res = await getAPI(
            `${endpoints.bundleMno}?country_code=${encodeURIComponent('+' + countryCode)}&phone_number=${firstTwoDigits}`
          )
          if (res?.error) {
            setErrors({ phone_number: res.data?.data?.message })
            setMnoData({})
            setButtonDisabled(true)
          } else {
            setErrors({})
            setMnoData(res?.data?.data || {})
            setButtonDisabled(false)
          }
        }, 300)
      }
    })(),
    [testing]
  )

  const handleInputChange = (id: string, value: any) => {
    if (id === 'country_code') {
      setPhoneNumber('')
      setCountryCode(value)
    }
    if (id === 'phone_number') {
      setPhoneNumber(value)
      if (value === '') {
        setErrors({})
      } else if (
        errors.phone_number !==
        'The telecom could not be detected based on the phone number you entered. Please try again.'
      ) {
        setErrors({})
      }
      const phoneWithoutCountryCode = value.slice(countryCode.length)

      if (phoneWithoutCountryCode.length < 2) {
        setPrevFirstTwoDigits('')
      }
      // Handle the case when a phone number is pasted and country code is empty
      if (value.length > 0 && countryCode === '') {
        const potentialCountryCode = value.slice(0, 2)
        setCountryCode(potentialCountryCode)
        const firstTwoDigits = value.slice(2, 4)
        if (firstTwoDigits.length === 2 && firstTwoDigits !== prevFirstTwoDigits) {
          setPrevFirstTwoDigits(firstTwoDigits)
          debouncedApiCall(potentialCountryCode, firstTwoDigits)
        }
      }
      // Only proceed if the value is longer than the country code
      else if (value.length > countryCode.length && countryCode !== '') {
        const firstTwoDigits = phoneWithoutCountryCode.slice(0, 2)
        const countryCodeLength = countryCode.length
        const countryCodeFromValue = value.slice(0, countryCodeLength)
        if (
          firstTwoDigits.length === 2 &&
          firstTwoDigits !== prevFirstTwoDigits &&
          countryCodeFromValue === countryCode
        ) {
          setPrevFirstTwoDigits(firstTwoDigits)
          debouncedApiCall(countryCode, firstTwoDigits)
        } else if (countryCodeFromValue !== countryCode) {
          setMnoData({})
          setButtonDisabled(true)
        }
      }
    }
  }

  const handleViewPlans = () => {
    if (!phoneNumber || !mnoData.id || !walletType) {
      setErrors({ phone_number: 'This field is required' })
      return
    }
    navigate('/services/bundle-activation/plans', {
      state: {
        mno_id: mnoData.id,
        telecom_name: mnoData.name,
        wallet_type: walletType,
        phone_number: phoneNumber,
        country_code: countryCode,
      },
    })
  }

  return (
    <div>
      <Breadcrumbs heading="Bundle Activation" showNavPath={false} />
      <AddModel padding="py-4 px-6">
        <AddInputs
          inputs={inputs}
          totalInputCount={2}
          inputValues={{
            phone_number: phoneNumber || '',
            telecom: mnoData.name || '',
          }}
          mapping={true}
          onInputChange={(id, value) => handleInputChange(id, value)}
          errors={errors}
        />
        <div className="bg-information_light rounded-lg p-2">
          <p className="text-body1 text-primary_text">
            Note: Telecom is auto-detected once you enter your phone number.
          </p>
        </div>
        {mnoData.id && (
          <div className="mt-6">
            <h1 className="text-body1 font-semibold text-primary_text mb-2">Wallet Type</h1>
            <CustomRadioButton
              options={WALLET_TYPES}
              selected={walletType}
              onChange={setWalletType}
              padding="py-3 pr-3"
              isDisabled={['digital_wallet']}
            />
          </div>
        )}
      </AddModel>
      <AddBottomButton
        buttonName="View Plans"
        buttonWidth="w-[125px]"
        onClick={handleViewPlans}
        loading={false}
        disabled={buttonDisabled || !walletType}
        testID="view-plans"
        align="end"
      />
    </div>
  )
}

export default BundleActivation
